/**
 * rPI OmniDesk — Inbox List
 *
 * Lists customer threads from `GET /api/v1/omni/threads` (WireSchema
 * `ThreadSummary`, safeParse-gated) with 60s polling matched to the Gmail
 * pull-cron cadence. Each row surfaces the enriched read fields: customer,
 * SLA-state badge, and an unread marker.
 */

import type {
  ThreadChannel,
  ThreadListFilters,
  ThreadStatus,
  ThreadSummary,
} from '@/api/porter-threads';
import { Button } from '@/components/ui';
import { usePorterThreads } from '@/hooks/use-porter-threads';
import { useAuth } from '@/hooks/useAuth';
import type { SlaState } from '@kgc/api-contract';
import { useTranslation } from '@kgc/i18n';
import type { JSX } from 'react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

const TEAL = '#14B8A6';

const CHANNEL_ICON: Record<ThreadChannel, string> = {
  EMAIL: '📧',
  WEBCHAT: '💬',
  CONTACT_FORM: '📝',
};

const STATUS_CLASSES: Record<ThreadStatus, string> = {
  OPEN: 'text-emerald-700 dark:text-emerald-300 border-emerald-400/40 bg-emerald-500/10',
  PENDING: 'text-amber-700 dark:text-amber-300 border-amber-400/40 bg-amber-500/10',
  SNOOZED: 'text-indigo-700 dark:text-indigo-300 border-indigo-400/40 bg-indigo-500/10',
  RESOLVED: 'text-slate-600 dark:text-slate-300 border-slate-400/40 bg-slate-500/10',
};

// SLA-badge colours (F-1a slaState, read-time computed). OK renders no badge.
const SLA_CLASSES: Record<SlaState, string> = {
  OK: '',
  DUE_SOON: 'text-amber-700 dark:text-amber-300 border-amber-400/40 bg-amber-500/10',
  BREACHED: 'text-red-700 dark:text-red-300 border-red-400/40 bg-red-500/10',
  ESCALATED: 'text-red-50 dark:text-red-50 border-red-600/60 bg-red-600/80',
};

type StatusFilterValue = ThreadStatus | 'ALL';
type ChannelFilterValue = ThreadChannel | 'ALL';
// F-2 RBAC nézet-váltó — tri-state assignee lens. ME → the current user's threads,
// UNASSIGNED → `assigneeUserId IS NULL`, ALL → no assignee filter (mutually exclusive wire).
type AssigneeFilterValue = 'ALL' | 'ME' | 'UNASSIGNED';

interface RelativeTimeFormatter {
  (iso: string): string;
}

function useRelativeTimeFormatter(): RelativeTimeFormatter {
  const { t } = useTranslation();

  return (iso: string): string => {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.round(diff / 60_000);
    if (mins < 1) return t('porter.inbox.relativeTime.now');
    if (mins < 60) return t('porter.inbox.relativeTime.minutes', { count: mins });
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return t('porter.inbox.relativeTime.hours', { count: hrs });
    const days = Math.round(hrs / 24);
    return t('porter.inbox.relativeTime.days', { count: days });
  };
}

function FilterCheckbox({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}): JSX.Element {
  return (
    <label className="flex cursor-pointer select-none items-center gap-2 py-1 text-sm hover:text-foreground">
      <input
        type="checkbox"
        checked={checked}
        onChange={e => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-teal-500/50 text-teal-500 focus:ring-teal-500"
      />
      <span className={checked ? 'text-foreground' : 'text-muted-foreground'}>{label}</span>
    </label>
  );
}

function ThreadCard({
  thread,
  active,
  formatRelative,
  statusLabel,
  slaLabel,
  unreadLabel,
  fallbackSubject,
}: {
  thread: ThreadSummary;
  active: boolean;
  formatRelative: RelativeTimeFormatter;
  statusLabel: string;
  slaLabel: string;
  unreadLabel: string;
  fallbackSubject: string;
}): JSX.Element {
  const activeClasses = active
    ? 'bg-teal-500/15 border-l-2 border-l-teal-400'
    : 'border-l-2 border-l-transparent hover:bg-teal-500/5';
  const subject = thread.subject ?? fallbackSubject;
  const customer = thread.customerName ?? thread.customerEmail;

  return (
    <Link
      to={`/porter/inbox/${thread.id}`}
      className={`block border-b border-teal-500/10 px-4 py-3 transition-colors ${activeClasses}`}
      data-testid="porter-thread-card"
    >
      <div className="flex items-center gap-2">
        {thread.unread && (
          <span
            className="h-2 w-2 shrink-0 rounded-full bg-teal-500"
            aria-label={unreadLabel}
            data-testid="porter-thread-unread"
          />
        )}
        <span className="text-sm">{CHANNEL_ICON[thread.channel]}</span>
        <span
          className={`flex-1 truncate text-sm text-foreground ${thread.unread ? 'font-bold' : 'font-semibold'}`}
        >
          {subject}
        </span>
        <span className="shrink-0 text-xs text-muted-foreground">
          {formatRelative(thread.lastMessageAt)}
        </span>
      </div>
      <div className="mt-1 flex items-center gap-2 pl-6">
        <span
          className={`inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px] font-medium ${STATUS_CLASSES[thread.status]}`}
        >
          {statusLabel}
        </span>
        {thread.slaState !== 'OK' && (
          <span
            className={`inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px] font-medium ${SLA_CLASSES[thread.slaState]}`}
            data-testid="porter-thread-sla"
          >
            {slaLabel}
          </span>
        )}
        {customer && <span className="truncate text-xs text-muted-foreground">{customer}</span>}
      </div>
    </Link>
  );
}

function ThreadCardSkeleton(): JSX.Element {
  return (
    <div className="block border-b border-teal-500/10 px-4 py-3" aria-hidden>
      <div className="flex items-center gap-2">
        <span className="h-4 w-4 shrink-0 animate-pulse rounded bg-slate-300/40 dark:bg-slate-600/40" />
        <span className="h-4 flex-1 animate-pulse rounded bg-slate-300/40 dark:bg-slate-600/40" />
        <span className="h-3 w-10 animate-pulse rounded bg-slate-300/40 dark:bg-slate-600/40" />
      </div>
      <div className="mt-2 h-3 w-2/3 animate-pulse rounded bg-slate-300/30 pl-6 dark:bg-slate-600/30" />
    </div>
  );
}

export function InboxListPage(): JSX.Element {
  const { t } = useTranslation();
  const formatRelative = useRelativeTimeFormatter();

  const { user } = useAuth();
  const currentUserId = user?.id;

  const [statusFilter, setStatusFilter] = useState<StatusFilterValue>('ALL');
  const [channelFilter, setChannelFilter] = useState<ChannelFilterValue>('ALL');
  const [assigneeFilter, setAssigneeFilter] = useState<AssigneeFilterValue>('ALL');
  const [search, setSearch] = useState('');

  // Only forward defined filters — avoids sending `status=ALL` to the API.
  // The assignee tri-state maps to mutually-exclusive params: ME → `assigneeUserId`,
  // UNASSIGNED → `unassigned`, ALL → neither (the BE DTO rejects both together). ME
  // needs a resolved user id — without it we send no assignee filter (authenticated
  // inbox always has one; the guard only protects a transient null-user render).
  const queryFilters = useMemo<ThreadListFilters>(() => {
    const f: ThreadListFilters = {};
    if (statusFilter !== 'ALL') f.status = statusFilter;
    if (channelFilter !== 'ALL') f.channel = channelFilter;
    if (assigneeFilter === 'ME' && currentUserId) f.assigneeUserId = currentUserId;
    if (assigneeFilter === 'UNASSIGNED') f.unassigned = true;
    return f;
  }, [statusFilter, channelFilter, assigneeFilter, currentUserId]);

  const { data, isLoading, isError, refetch, isFetching } = usePorterThreads(queryFilters);

  const threads = data?.data ?? [];

  // Client-side subject search — server currently has no search param.
  const visible = useMemo(() => {
    if (!search.trim()) return threads;
    const needle = search.toLowerCase();
    return threads.filter(t => (t.subject ?? '').toLowerCase().includes(needle));
  }, [threads, search]);

  const statusOptions: StatusFilterValue[] = ['ALL', 'OPEN', 'PENDING', 'SNOOZED', 'RESOLVED'];
  const channelOptions: ChannelFilterValue[] = ['ALL', 'EMAIL', 'WEBCHAT', 'CONTACT_FORM'];
  const assigneeOptions: AssigneeFilterValue[] = ['ALL', 'ME', 'UNASSIGNED'];

  return (
    <div className="flex h-[calc(100vh-64px)] flex-col">
      {/* Header */}
      <div className="border-b border-teal-500/20 kgc-card-bg">
        <div className="flex items-center justify-between gap-4 px-4 pt-3">
          <div className="flex items-center gap-3">
            <div
              className="flex h-9 w-9 items-center justify-center rounded-lg text-lg text-white shadow-sm"
              style={{ backgroundColor: TEAL }}
            >
              📬
            </div>
            <div>
              <h1 className="text-lg font-bold text-foreground">{t('porter.inbox.title')}</h1>
              {isFetching && !isLoading && (
                <p className="text-xs text-muted-foreground">{t('porter.inbox.loading')}</p>
              )}
            </div>
          </div>
          <div className="relative max-w-md flex-1">
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('porter.inbox.searchPlaceholder')}
              className="w-full rounded-md border border-slate-300 bg-white py-2 pl-9 pr-3 text-sm outline-none transition-colors focus:border-teal-500 dark:border-slate-600 dark:bg-slate-900/60"
            />
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
              🔍
            </span>
          </div>
        </div>
        <div className="px-4 py-2 text-xs text-muted-foreground">
          {t('porter.inbox.pollingHint')}
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Left filters sidebar */}
        <aside className="w-56 shrink-0 overflow-y-auto border-r border-teal-500/20 p-4 kgc-card-bg">
          <h3 className="mb-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">
            {t('porter.inbox.filters')}
          </h3>

          <div className="mb-4" data-testid="porter-inbox-assignee-filter">
            <div className="mb-1.5 text-xs font-semibold text-foreground">
              {t('porter.inbox.assigneeLabel')}
            </div>
            {assigneeOptions.map(a => (
              <FilterCheckbox
                key={a}
                label={t(`porter.inbox.filter.assignee.${a.toLowerCase()}`)}
                checked={assigneeFilter === a}
                onChange={() => setAssigneeFilter(a)}
              />
            ))}
          </div>

          <div className="mb-4">
            <div className="mb-1.5 text-xs font-semibold text-foreground">
              {t('porter.inbox.statusLabel')}
            </div>
            {statusOptions.map(s => (
              <FilterCheckbox
                key={s}
                label={t(`porter.inbox.filter.status.${s === 'ALL' ? 'all' : s}`)}
                checked={statusFilter === s}
                onChange={() => setStatusFilter(s)}
              />
            ))}
          </div>

          <div className="mb-4">
            <div className="mb-1.5 text-xs font-semibold text-foreground">
              {t('porter.inbox.channelLabel')}
            </div>
            {channelOptions.map(c => (
              <FilterCheckbox
                key={c}
                label={t(`porter.inbox.filter.channel.${c === 'ALL' ? 'all' : c}`)}
                checked={channelFilter === c}
                onChange={() => setChannelFilter(c)}
              />
            ))}
          </div>
        </aside>

        {/* Thread list */}
        <div className="flex w-[420px] shrink-0 flex-col overflow-hidden border-r border-teal-500/20">
          <div className="flex items-center justify-between border-b border-teal-500/20 px-4 py-2 kgc-card-bg">
            <div className="text-sm font-semibold text-foreground">
              {t('porter.inbox.threadCount', { count: visible.length })}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto" data-testid="porter-inbox-list">
            {isLoading ? (
              <div data-testid="porter-inbox-skeleton">
                {Array.from({ length: 5 }).map((_, idx) => (
                  <ThreadCardSkeleton key={idx} />
                ))}
              </div>
            ) : isError ? (
              <ErrorBanner onRetry={() => void refetch()} />
            ) : visible.length === 0 ? (
              <EmptyState totalCount={threads.length} />
            ) : (
              visible.map(thread => (
                <ThreadCard
                  key={thread.id}
                  thread={thread}
                  active={false}
                  formatRelative={formatRelative}
                  statusLabel={t(`porter.inbox.filter.status.${thread.status}`)}
                  slaLabel={t(`porter.inbox.sla.${thread.slaState}`)}
                  unreadLabel={t('porter.inbox.unread')}
                  fallbackSubject={t('porter.inbox.noSubject')}
                />
              ))
            )}
          </div>
        </div>

        {/* Right preview pane — empty state */}
        <div
          className="flex flex-1 items-center justify-center p-8"
          style={{
            background:
              'radial-gradient(circle at 50% 40%, rgba(20,184,166,0.06) 0%, transparent 60%)',
          }}
        >
          <div className="text-center">
            <div
              className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full border border-teal-500/30 text-3xl"
              style={{ backgroundColor: 'rgba(20,184,166,0.08)' }}
            >
              📬
            </div>
            <h2 className="mb-1 text-base font-semibold text-foreground">
              {t('porter.inbox.previewHint.title')}
            </h2>
            <p className="max-w-xs text-sm text-muted-foreground">
              {t('porter.inbox.previewHint.description')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Sally onboarding empty state (critical UX point from Round 2 party mode):
 * the first franchise opens zero threads — don't scare them with a dead inbox,
 * offer the Gmail/webchat setup path + a demo tour.
 */
function EmptyState({ totalCount }: { totalCount: number }): JSX.Element {
  const { t } = useTranslation();

  // If filters hid everything (not truly-empty inbox), show a lighter message.
  if (totalCount > 0) {
    return (
      <div
        className="flex h-full flex-col items-center justify-center p-6 text-center"
        data-testid="porter-inbox-filtered-empty"
      >
        <div className="mb-3 text-4xl opacity-50">🔍</div>
        <div className="mb-1 text-sm font-medium text-foreground">
          {t('porter.inbox.filteredEmpty.title')}
        </div>
        <div className="text-xs text-muted-foreground">
          {t('porter.inbox.filteredEmpty.description')}
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex h-full flex-col items-center justify-center px-6 py-12 text-center"
      data-testid="porter-inbox-empty"
    >
      <div className="mb-4 text-5xl opacity-80">📬</div>
      <h3 className="text-lg font-medium text-foreground">{t('porter.inbox.empty.title')}</h3>
      <p className="mt-2 max-w-sm text-sm text-muted-foreground">
        {t('porter.inbox.empty.description')}
      </p>
      <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-center">
        <Link
          to="/porter/settings/channels"
          className="inline-flex h-10 items-center justify-center rounded-md bg-teal-600 px-4 text-sm font-medium text-white transition-colors hover:bg-teal-700"
        >
          {t('porter.inbox.empty.ctaSetup')}
        </Link>
        <Link
          to="/public/porter-prototype"
          className="inline-flex h-10 items-center justify-center rounded-md border border-teal-500/40 bg-teal-500/10 px-4 text-sm font-medium text-teal-700 transition-colors hover:bg-teal-500/20 dark:text-teal-300"
        >
          {t('porter.inbox.empty.ctaDemo')}
        </Link>
      </div>
    </div>
  );
}

function ErrorBanner({ onRetry }: { onRetry: () => void }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div
      className="m-4 rounded-md border border-red-400/40 bg-red-500/10 p-4 text-sm"
      data-testid="porter-inbox-error"
      role="alert"
    >
      <div className="mb-2 font-medium text-red-700 dark:text-red-300">
        {t('porter.inbox.error.title')}
      </div>
      <p className="mb-3 text-xs text-muted-foreground">{t('porter.inbox.error.description')}</p>
      <Button size="sm" variant="outline" onClick={onRetry}>
        {t('porter.inbox.error.retry')}
      </Button>
    </div>
  );
}
